// src/Container/ChangePassword/ChangePassword.js

import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';

import Portal from '@mui/material/Portal';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import { auth } from '../../Firebase/firebase';
import {
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from 'firebase/auth';

import { useNavigate } from 'react-router-dom';

import ConfirmDialog from '../../Components/ConfirmDialog/ConfirmDialog';

export default function ChangePassword() {
  const navigate = useNavigate();
  const user = auth.currentUser;

  // Inline alert system
  const [alert, setAlert] = useState({
    visible: false,
    severity: '',
    message: '',
  });

  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false); // NEW: Visual loading state

  const showAlert = (severity, message) => {
    setAlert({ visible: true, severity, message });
    setTimeout(() => {
      setAlert({ visible: false, severity: '', message: '' });
    }, 2000);
  };

  // Form fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  // Eye toggles
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Google provider check
  const isGoogleUser = user?.providerData[0]?.providerId === 'google.com';

  // Redirect Google user immediately
  useEffect(() => {
    if (isGoogleUser) {
      navigate('/', {
        state: {
          globalAlert: {
            severity: 'error',
            message: 'Google users cannot change their password here.',
          },
        },
      });
    }
  }, [isGoogleUser, navigate]);

  if (isGoogleUser) return null;

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmNewPassword) {
      showAlert('error', 'New passwords do not match.');
      return;
    }

    if (currentPassword === newPassword) {
      showAlert('error', 'New password must be different from current password.');
      return;
    }

    setIsUpdating(true); // Trigger the visual loading state

    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      await updatePassword(user, newPassword);

      // Trigger the 3-second success dialog
      setSuccessDialogOpen(true);
    } catch (error) {
      setIsUpdating(false); // Only remove the loading state if there is an error
      let msg = error.message;
      if (msg.includes('auth/wrong-password')) msg = 'Current password is incorrect.';
      if (msg.includes('auth/weak-password'))
        msg = 'Password must be at least 6 characters.';
      showAlert('error', msg);
    }
  };

  // Automatically triggered after 3 seconds by the ConfirmDialog
  const handleSuccessClose = () => {
    setSuccessDialogOpen(false);
    navigate('/');
  };

  const cancelButtonSx = {
    textTransform: 'none',
    minWidth: 120,
    borderWidth: 2,
    fontWeight: 500,
    '&:hover': {
      borderWidth: 2,
      backgroundColor: 'rgba(255,0,0,0.08)',
    },
    '&.Mui-disabled': {
      borderColor: '#d32f2f',
      color: '#d32f2f',
      opacity: 0.5,
    },
  };

  return (
    <Portal>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',

          display: 'flex',
          justifyContent: 'center', // Center Horizontally
          alignItems: 'flex-start', // Start from Top
          paddingTop: '150px',

          zIndex: 100010, // Elevated to Global System Overlays tier
          backgroundColor: 'transparent',
          backdropFilter: 'none',
        }}>
        <ConfirmDialog
          open={successDialogOpen}
          onClose={handleSuccessClose}
          message='Password updated successfully!'
          severity='success'
          autoClose={3000} // This removes the tick and triggers the countdown
          disableBackdropClick={true} // Forces the user to wait the 3 seconds
          sx={{ zIndex: 100015 }} // Kept securely above the main container
        />

        <Box
          sx={{
            width: '100%',
            maxWidth: 400,
            bgcolor: 'white',
            borderRadius: 3,
            boxShadow: 6,
            p: 4,
            position: 'relative',
          }}>
          {alert.visible && (
            <Box sx={{ mb: 2 }}>
              <Alert severity={alert.severity}>{alert.message}</Alert>
            </Box>
          )}

          <Typography variant='h5' sx={{ mb: 3, textAlign: 'center', fontWeight: 600 }}>
            Change Password
          </Typography>

          <Box component='form' onSubmit={handleSubmit}>
            <input
              type='text'
              name='username'
              autoComplete='username'
              value={user?.email || ''}
              style={{ display: 'none' }}
              readOnly
              aria-hidden='true'
            />

            <TextField
              label='Current Password'
              type={showCurrent ? 'text' : 'password'}
              fullWidth
              required
              margin='normal'
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete='current-password'
              disabled={isUpdating}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      onClick={() => setShowCurrent(!showCurrent)}
                      disabled={isUpdating}>
                      {showCurrent ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label='New Password'
              type={showNew ? 'text' : 'password'}
              fullWidth
              required
              margin='normal'
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete='new-password'
              disabled={isUpdating}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      onClick={() => setShowNew(!showNew)}
                      disabled={isUpdating}>
                      {showNew ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label='Confirm New Password'
              type={showConfirm ? 'text' : 'password'}
              fullWidth
              required
              margin='normal'
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              autoComplete='new-password'
              disabled={isUpdating}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      onClick={() => setShowConfirm(!showConfirm)}
                      disabled={isUpdating}>
                      {showConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 3,
              }}>
              <Button
                type='submit'
                variant='contained'
                disabled={isUpdating}
                sx={{
                  textTransform: 'none',
                  minWidth: 120,
                  '&:hover': {
                    backgroundColor: '#1565c0',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                    transform: 'scale(1.03)',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#1976d2',
                    color: '#fff',
                  },
                }}>
                {isUpdating && (
                  <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                )}
                Update
              </Button>

              <Button
                variant='outlined'
                color='error'
                disabled={isUpdating}
                onClick={() => navigate('/')}
                sx={cancelButtonSx}>
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Portal>
  );
}
